<template>
  <div>
    <v-dialog v-model="dialog" persistent scrollable max-width="720px">
      <v-card v-if="Student" v-draggable-dialog class="profile-dialog-card student-edit-dialog">
        <div class="dialog-drag-handle profile-dialog-header student-edit-dialog__header">
          <div class="student-edit-dialog__title">
            <v-avatar size="44" color="green lighten-5">
              <v-icon color="primary">mdi-account-edit-outline</v-icon>
            </v-avatar>
            <div>
              <span>Edit student</span>
              <h2>{{ studentDisplayName }}</h2>
              <p>Update personal details and review current assignments.</p>
            </div>
          </div>
          <div class="student-edit-dialog__header-actions">
            <v-icon small color="grey lighten-1" aria-hidden="true">mdi-drag</v-icon>
            <v-btn icon aria-label="Close edit student" @click.stop="close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider />

        <v-card-text class="profile-dialog-body student-edit-dialog__body">
          <section class="student-edit-section">
            <div class="student-edit-section__heading">
              <span><v-icon size="19" color="primary">mdi-account-outline</v-icon></span>
              <div>
                <h3>Student information</h3>
                <p>Basic details used throughout Taleem.</p>
              </div>
            </div>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.trim="Student.firstName"
                  outlined
                  dense
                  autofocus
                  :error-messages="nameErrors"
                  label="First name"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model.trim="Student.lastName" outlined dense label="Last name" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="Student.gender"
                  :items="genderOptions"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="Gender"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="Student.dob"
                  outlined
                  dense
                  type="date"
                  :max="maxDob"
                  label="Date of birth"
                  persistent-hint
                  hint="Optional"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model.trim="Student.phoneNumber" outlined dense label="Phone number" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model.trim="Student.email" outlined dense label="Email address" />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model.trim="Student.reference" outlined dense label="Reference" />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="Student.description"
                  outlined
                  dense
                  auto-grow
                  rows="2"
                  label="Description"
                />
              </v-col>
            </v-row>
          </section>

          <section class="student-edit-section student-edit-section--assignments">
            <div class="student-edit-section__heading">
              <span><v-icon size="19" color="primary">mdi-school-outline</v-icon></span>
              <div>
                <h3>Departments</h3>
                <p>Departments and fees currently assigned to this student.</p>
              </div>
              <span class="student-edit-count">{{ Classes.length }}</span>
            </div>

            <div v-if="Classes.length" class="student-edit-assignment-list">
              <div v-for="item in Classes" :key="`department-${item.id}`" class="student-edit-assignment">
                <span class="student-edit-assignment__icon">
                  <v-icon size="18" :color="item.countInSubscription ? 'primary' : 'grey'">
                    {{ item.countInSubscription ? "mdi-check-decagram" : "mdi-school-outline" }}
                  </v-icon>
                </span>
                <div class="student-edit-assignment__copy">
                  <strong>{{ item.Class ? item.Class.name : "Department" }}</strong>
                  <small>{{ item.parentPay | currency }} parent responsibility</small>
                </div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small aria-label="Edit department fee" v-bind="attrs" v-on="on" @click="editFeeOpen(item)">
                      <v-icon size="18">mdi-pencil-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit fee</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="error" aria-label="Remove department" v-bind="attrs" v-on="on" @click="deleteStudent(item)">
                      <v-icon size="18">mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Remove department</span>
                </v-tooltip>
              </div>
            </div>
            <div v-else class="student-edit-empty">No departments assigned.</div>
          </section>

          <section class="student-edit-section student-edit-section--assignments">
            <div class="student-edit-section__heading">
              <span><v-icon size="19" color="primary">mdi-google-classroom</v-icon></span>
              <div>
                <h3>Classrooms</h3>
                <p>Classrooms where this student is currently enrolled.</p>
              </div>
              <span class="student-edit-count">{{ Classrooms.length }}</span>
            </div>

            <div v-if="Classrooms.length" class="student-edit-assignment-list">
              <div v-for="item in Classrooms" :key="`classroom-${item.id}`" class="student-edit-assignment">
                <span class="student-edit-assignment__icon">
                  <v-icon size="18" color="primary">mdi-door-open</v-icon>
                </span>
                <div class="student-edit-assignment__copy">
                  <strong>{{ item.ClassRoom ? item.ClassRoom.name : "Classroom" }}</strong>
                  <small>Current classroom</small>
                </div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="error" aria-label="Remove classroom" v-bind="attrs" v-on="on" @click="removeClassroom(item)">
                      <v-icon size="18">mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Remove classroom</span>
                </v-tooltip>
              </div>
            </div>
            <div v-else class="student-edit-empty">No classrooms assigned.</div>
          </section>
        </v-card-text>

        <v-divider />
        <v-card-actions class="profile-dialog-actions student-edit-dialog__actions">
          <v-btn text @click="close">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="loading" @click="submit">
            <v-icon left small>mdi-content-save-outline</v-icon>
            Save changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <EditFee
      :editDialog="feeDialog"
      :editStudent="selectedStudent"
      @close="feeDialog = false"
      @submit="editFeeSubmit"
    />
  </div>
</template>

<script>
import studentService from "@/modules/Student/service.js";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");
const EditFee = () => import("@/modules/Student/components/EditFee.vue");

function emptyStudent() {
  return {
    firstName: null,
    lastName: null,
    email: null,
    gender: "male",
    dob: null,
    phoneNumber: null,
    reference: null,
    description: null,
  };
}

export default {
  name: "update-Student",
  components: { EditFee },
  mixins: [validationMixin],
  props: {
    editDialog: {
      default: false,
    },
    editStudent: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      Classrooms: [],
      Classes: [],
      Student: emptyStudent(),
      dialog: false,
      feeDialog: false,
      loading: false,
      selectedStudent: null,
      genderOptions: [
        { text: "Male", value: "male" },
        { text: "Female", value: "female" },
      ],
    };
  },
  validations: {
    Student: {
      firstName: { required },
    },
  },
  computed: {
    maxDob() {
      return this.$moment().format("YYYY-MM-DD");
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.Student.firstName.$dirty) return errors;
      if (!this.$v.Student.firstName.required) errors.push("First name is required");
      return errors;
    },
    studentDisplayName() {
      const fullName = [this.Student.firstName, this.Student.lastName].filter(Boolean).join(" ").trim();
      return fullName || "Student details";
    },
  },
  watch: {
    editDialog(val) {
      this.dialog = val;
      if (!val || !this.editStudent) return;

      this.Student = {
        ...this.editStudent,
        dob: this.editStudent.dob ? this.$moment(this.editStudent.dob).format("YYYY-MM-DD") : null,
      };
      this.getClasses();
      this.getClassrooms();
    },
  },
  methods: {
    editFeeSubmit() {
      this.feeDialog = false;
      this.getClasses();
      this.$emit("submit");
    },
    editFeeOpen(item) {
      this.selectedStudent = item;
      this.feeDialog = true;
    },
    removeClassroom(obj) {
      this.$swal({
        title: "Remove classroom?",
        text: `Remove ${this.studentDisplayName} from ${obj.ClassRoom.name}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (!result.value) return;
        studentService.removeStudentFromClassRoom(obj.id).then((response) => {
          if (response.data) {
            this.$swal("Removed", "The classroom assignment was removed.", "success");
            this.getClassrooms();
            this.$emit("submit");
          }
        });
      });
    },
    deleteStudent(obj) {
      const departmentName = obj.Class ? obj.Class.name : "this department";
      this.$swal({
        title: "Remove department?",
        text: `Remove ${this.studentDisplayName} from ${departmentName}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (!result.value) return;
        studentService.removeStudentFromClass(obj.id).then((response) => {
          if (response.data) {
            this.$swal("Removed", "The department assignment was removed.", "success");
            this.getClasses();
            this.$emit("submit");
          }
        });
      });
    },
    getClasses() {
      return studentService.getAllClassStudent({ StudentId: this.editStudent.id }).then((response) => {
        this.Classes = response.data || [];
        return response;
      });
    },
    getClassrooms() {
      return studentService.getStudentByClassRoomId({ StudentId: this.editStudent.id }).then((response) => {
        this.Classrooms = response.data || [];
        return response;
      });
    },
    close() {
      this.dialog = false;
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.Student = emptyStudent();
      this.Classes = [];
      this.Classrooms = [];
      this.selectedStudent = null;
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        }).fire({
          icon: "error",
          title: this.$t("toastMessage.text.invalidFields"),
        });
        return;
      }

      this.loading = true;
      return studentService.update(this.Student.id, this.Student)
        .then((result) => {
          if (result.status) {
            this.$swal.mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            }).fire({ icon: "success", title: "Student is updated" });
            this.$emit("submit");
            this.close();
          }
        })
        .catch((err) => {
          this.$swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          }).fire({
            icon: "error",
            title: err && err.data ? err.data.message : "Student could not be updated",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.student-edit-dialog__header {
  background: linear-gradient(135deg, #f7fbf8 0%, #edf7ef 100%);
}

.student-edit-dialog__title,
.student-edit-dialog__header-actions,
.student-edit-section__heading,
.student-edit-assignment {
  display: flex;
  align-items: center;
}

.student-edit-dialog__title {
  min-width: 0;
  gap: 12px;
}

.student-edit-dialog__title > div {
  min-width: 0;
}

.student-edit-dialog__title span {
  color: #2f7d4a;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.student-edit-dialog__title h2 {
  overflow: hidden;
  margin: 1px 0 0;
  color: #253047;
  font-size: 18px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-edit-dialog__title p {
  margin: 2px 0 0;
  color: #7f8c82;
  font-size: 10px;
}

.student-edit-dialog__header-actions {
  flex: 0 0 auto;
  gap: 5px;
}

.student-edit-dialog__body {
  background: #f8faf8;
}

.student-edit-section {
  margin-bottom: 14px;
  padding: 17px;
  border: 1px solid #e2e9e3;
  border-radius: 12px;
  background: #fff;
}

.student-edit-section:last-child {
  margin-bottom: 0;
}

.student-edit-section__heading {
  gap: 10px;
  margin-bottom: 15px;
}

.student-edit-section__heading > span:first-child,
.student-edit-assignment__icon {
  display: flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #edf7ef;
}

.student-edit-section__heading > div {
  min-width: 0;
  flex: 1;
}

.student-edit-section__heading h3 {
  margin: 0;
  color: #293448;
  font-size: 14px;
}

.student-edit-section__heading p {
  margin: 2px 0 0;
  color: #8a958d;
  font-size: 10px;
}

.student-edit-count {
  display: inline-flex;
  min-width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #2f7d4a;
  background: #e7f4ea;
  font-size: 11px;
  font-weight: 800;
}

.student-edit-section ::v-deep .v-text-field--outlined .v-input__slot {
  border-radius: 9px;
}

.student-edit-assignment-list {
  overflow: hidden;
  border: 1px solid #e7ece8;
  border-radius: 10px;
}

.student-edit-assignment {
  min-height: 62px;
  gap: 10px;
  padding: 9px 10px;
  border-bottom: 1px solid #edf1ee;
}

.student-edit-assignment:last-child {
  border-bottom: 0;
}

.student-edit-assignment__icon {
  width: 34px;
  height: 34px;
  flex-basis: 34px;
  border-radius: 9px;
}

.student-edit-assignment__copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.student-edit-assignment__copy strong {
  overflow: hidden;
  color: #344139;
  font-size: 12px;
  text-transform: capitalize;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.student-edit-assignment__copy small {
  margin-top: 2px;
  color: #8b968e;
  font-size: 9px;
}

.student-edit-empty {
  padding: 18px;
  border: 1px dashed #dce4dd;
  border-radius: 10px;
  color: #939d95;
  background: #fafcfb;
  font-size: 10px;
  text-align: center;
}

.student-edit-dialog__actions {
  background: #fff;
}

@media (max-width: 599px) {
  .student-edit-dialog__header {
    min-height: 70px;
    padding: 13px 14px;
  }

  .student-edit-dialog__title p,
  .student-edit-dialog__header-actions > .v-icon {
    display: none;
  }

  .student-edit-dialog__body {
    padding: 14px !important;
  }

  .student-edit-section {
    padding: 14px;
  }
}
</style>
